import { useState, useEffect, useCallback } from 'react';
import { UnitOfMeasure, UnitOfMeasureListResponse, UnitOfMeasureCreate, UnitOfMeasureUpdate } from '../types/costwand.d';
import { apiClient, ApiError } from '../api/apiClient.tsx';

const PAGE_SIZE = 10;

interface UseUnitsOfMeasureResult {
  unitsOfMeasure: UnitOfMeasure[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  fetchUnitsOfMeasure: (page: number) => Promise<void>;
  handlePageChange: (page: number) => void;
  createUnitOfMeasure: (data: UnitOfMeasureCreate) => Promise<boolean>;
  updateUnitOfMeasure: (id: number, data: UnitOfMeasureUpdate) => Promise<boolean>;
  deleteUnitOfMeasure: (id: number) => Promise<boolean>;
}

export const useUnitsOfMeasure = (): UseUnitsOfMeasureResult => {
  const [unitsOfMeasure, setUnitsOfMeasure] = useState<UnitOfMeasure[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUnitsOfMeasure = useCallback(async (page: number) => {
    if (page < 1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient<UnitOfMeasureListResponse>(
        `/units_of_measure?page=${page}&page_size=${PAGE_SIZE}`,
        { bypassCache: true }
      );
      setUnitsOfMeasure(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch units of measure: ${err.message}`);
      } else {
        setError('An unknown error occurred while fetching units of measure.');
      }
      setUnitsOfMeasure([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnitsOfMeasure(currentPage);
  }, [currentPage, fetchUnitsOfMeasure]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const createUnitOfMeasure = async (data: UnitOfMeasureCreate): Promise<boolean> => {
    setError(null);
    try {
      const payload = { ...data };
      await apiClient<UnitOfMeasure>('/units_of_measure', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      await fetchUnitsOfMeasure(1); // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      return false;
    }
  };

  const updateUnitOfMeasure = async (id: number, data: UnitOfMeasureUpdate): Promise<boolean> => {
    setError(null);
    try {
      const payload = { ...data };
      await apiClient<UnitOfMeasure>(`/units_of_measure/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      await fetchUnitsOfMeasure(currentPage); // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      return false;
    }
  };

  const deleteUnitOfMeasure = async (id: number): Promise<boolean> => {
    if (!window.confirm(`Are you sure you want to delete Unit of Measure ID: ${id}?`)) return false;
    setError(null);
    try {
      await apiClient<void>(`/units_of_measure/${id}`, { method: 'DELETE' });
      const pageToFetch = unitsOfMeasure.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      await fetchUnitsOfMeasure(pageToFetch); // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      return false;
    }
  };

  return {
    unitsOfMeasure,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    fetchUnitsOfMeasure,
    handlePageChange,
    createUnitOfMeasure,
    updateUnitOfMeasure,
    deleteUnitOfMeasure,
  };
};